import { create } from "zustand";
import axios from "axios";
import { LOG } from "../utils/logUtil";
import { useAlert } from "./useAlert";
import { useAppLoading } from "./useAppLoading";
import { useCommunities } from "./useCommunities";

export interface IWithdraw {
  _id?: string;
  amount?: number;
  community?: string;
  user?: string;
  createdAt?: Date;
}

interface IDonation {
  donationId: string;
  amount: number;
  createdAt: string;
  user: {
    _id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
  };
  isPartOfLottery: boolean;
  lotteryTitle?: {
    en_title: string;
    nl_title: string;
  } | null;
}

interface IDonationsResponse {
  data: IDonation[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface IStore {
  /** State */
  withdraws: IWithdraw[];
  donations: IDonation[];
  hasNextPage: boolean;
  hasNextDonations: boolean;
  createdWithdraw: string | null;
  /** Loaders */
  isWithdrawsLoading: boolean;
  isWithdrawLoading: boolean;
  isCreatingWithdrawLoading: boolean;
  /** Callbacks */
  getWithdraws: (communityId: string, page?: number, limit?: number) => void;
  createWithdraw: (withdraw: IWithdraw) => void;
  deleteWithdraw: (withdrawId: string, communityId: string) => void;
  getDonations: (communityId: string, page: number, limit: number) => void;
  clearDonations: () => void;
  clearCreatedWithdraw: () => void;
}

export const useCommunityWithdraws = create<IStore>()((set, get) => ({
  /** State */
  withdraws: [],
  donations: [],
  hasNextPage: true,
  hasNextDonations: false,
  createdWithdraw: null,
  /** Loaders */
  isWithdrawsLoading: false,
  isWithdrawLoading: false,
  isCreatingWithdrawLoading: false,
  /** Callbacks */
  getWithdraws: async (communityId, page = 1, limit = 10) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(
        `withdraws/community/byCommunityId/${communityId}?page=${page}&limit=${limit}`
      );
      if (response?.data) {
        set((state) => ({
          ...state,
          withdraws:
            page === 1
              ? response?.data
              : [...response?.data, ...state.withdraws],
          hasNextPage: response?.data?.length === limit,
        }));
      }
    } catch (error) {
      LOG.error("Error on getting withdraws", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  createWithdraw: async (withdraw: IWithdraw) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("withdraws/community", withdraw);
      if (response?.data) {
        useAlert.getState().setAlert({
          isOpen: true,
          message: "Withdraw created successfully",
          severity: "success",
        });
      }
      if (response?.data?.message) {
        useAlert.getState().setAlert({
          isOpen: true,
          message: response?.data?.message,
          severity: "error",
        });
      }
    } catch (error) {
      LOG.error("Error on creating withdraw", { error });
    } finally {
      useCommunities.getState().getCommunity(withdraw.community as string);
      get().getWithdraws(withdraw.community as string);
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteWithdraw: async (withdrawId, communityId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`withdraws/community/${withdrawId}`);
      if (response?.data) {
        useAlert.getState().setAlert({
          isOpen: true,
          message: "Withdraw deleted successfully",
          severity: "success",
        });
      }
      if (response?.data?.message) {
        useAlert.getState().setAlert({
          isOpen: true,
          message: response?.data?.message,
          severity: "error",
        });
      }
    } catch (error) {
      LOG.error("Error on creating withdraw", { error });
    } finally {
      useCommunities.getState().getCommunity(communityId as string);
      get().getWithdraws(communityId as string);
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getDonations: async (communityId, page, limit) => {
    try {
      const response = await axios.get<IDonationsResponse>(
        `/payments/community/${communityId}/donates?page=${page}&limit=${limit}`
      );
      if (response?.data) {
        set((state) => ({
          hasNextDonations: response.data.data.length >= limit,
          donations:
            page === 1
              ? response.data.data
              : [...state.donations, ...response.data.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on fetching community donations", error);
    }
  },
  clearDonations: () => set({ donations: [], hasNextDonations: false }),
  clearCreatedWithdraw: () => {},
}));
